import { useCallback, useSyncExternalStore } from 'react';

import {
  DEFAULT_THEME_PREFERENCE,
  THEME_PREFS_STORAGE_KEY,
  readThemePrefs,
  resolveTheme,
  writeThemePrefs,
} from '#/utils/themePrefsStorage';

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

const listeners = new Set();

let initialized = false;
let snapshot = { preference: DEFAULT_THEME_PREFERENCE, resolvedTheme: 'light' };

function systemPrefersDark() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(DARK_MEDIA_QUERY).matches;
}

function applyToDocument(resolvedTheme) {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  root.classList.toggle('dark', resolvedTheme === 'dark');
  root.style.colorScheme = resolvedTheme;
}

function commit(preference) {
  const resolvedTheme = resolveTheme(preference, systemPrefersDark());
  applyToDocument(resolvedTheme);

  if (snapshot.preference === preference && snapshot.resolvedTheme === resolvedTheme) {
    return;
  }

  snapshot = { preference, resolvedTheme };
  listeners.forEach((listener) => listener());
}

/**
 * 啟動時呼叫一次（main.jsx）：套用已存偏好，並監聽系統主題與其他分頁的變更。
 * 使用者可從導覽列 ThemeToggle 切換；dev 亦可使用
 * window.__setTheme('dark' | 'light' | 'system')。
 * index.html 另有同步的預先套用 script，避免首次繪製閃白。
 */
export function initTheme() {
  if (initialized) {
    return;
  }
  initialized = true;

  commit(readThemePrefs()?.preference ?? DEFAULT_THEME_PREFERENCE);

  if (typeof window === 'undefined') {
    return;
  }

  if (import.meta.env.DEV) {
    window.__setTheme = (preference) => {
      if (writeThemePrefs(preference)) {
        commit(preference);
      }
    };
  }

  if (typeof window.matchMedia === 'function') {
    window.matchMedia(DARK_MEDIA_QUERY).addEventListener('change', () => {
      commit(snapshot.preference);
    });
  }

  window.addEventListener('storage', (event) => {
    if (event.key === THEME_PREFS_STORAGE_KEY) {
      commit(readThemePrefs()?.preference ?? DEFAULT_THEME_PREFERENCE);
    }
  });
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return snapshot;
}

/**
 * @returns {{
 *   preference: 'light'|'dark'|'system',
 *   resolvedTheme: 'light'|'dark',
 *   setPreference: (preference: 'light'|'dark'|'system') => void,
 * }}
 */
export function useTheme() {
  const { preference, resolvedTheme } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setPreference = useCallback((next) => {
    if (writeThemePrefs(next)) {
      commit(next);
    }
  }, []);

  return { preference, resolvedTheme, setPreference };
}
